import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import MyCompanyService from "../services/MyCompanyService";
import SocieteAndUserService from "../services/societeAndUser";
import ClientContractService from "../services/ClientContractService";
import SubscriptionPaymentService from "../services/SubscriptionPaymentService";
import SubscriptionPaymentDialog from "../components/ui/SubscriptionPaymentDialog";
import UtilsUrl from "../utils/UtilsUrl";

const EMPTY = {
  title: "",
  tradeName: "",
  matricule: "",
  commerceRegister: "",
  legalForm: "",
  activitySector: "",
  adresse: "",
  city: "",
  postalCode: "",
  country: "",
  numPhone: "",
  email: "",
  website: "",
  managerName: "",
  managerPhone: "",
  managerEmail: "",
  timbre: "",
};

// the stored logo is a path relative to the gateway (like the product images)
const logoSrc = (url) =>
  url && !/^(https?:|blob:|data:)/.test(url) ? UtilsUrl.fileRetailURL() + url : url;

const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const LOGO_TYPES = ["image/png", "image/jpeg"];

const EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const PHONE = /^[0-9+()\-. ]{6,20}$/;
const WEBSITE = /^(https?:\/\/)?[A-Za-z0-9.-]+\.[A-Za-z]{2,}(\/\S*)?$/;
const POSTAL = /^[A-Za-z0-9 -]{3,10}$/;
const MATRICULE = /^[A-Za-z0-9/ .-]{1,50}$/;

const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};

const toForm = (company) => {
  const form = { ...EMPTY };
  Object.keys(EMPTY).forEach((key) => {
    const value = company[key];
    form[key] = value === null || value === undefined ? "" : String(value);
  });
  return form;
};

const validate = (form) => {
  const errors = {};
  const v = (key) => form[key].trim();
  if (!v("title")) errors.title = "La raison sociale est obligatoire";
  if (v("matricule") && !MATRICULE.test(v("matricule")))
    errors.matricule = "Matricule fiscal invalide";
  if (v("postalCode") && !POSTAL.test(v("postalCode")))
    errors.postalCode = "Code postal invalide";
  if (v("numPhone") && !PHONE.test(v("numPhone")))
    errors.numPhone = "Numéro de téléphone invalide";
  if (v("email") && !EMAIL.test(v("email")))
    errors.email = "Adresse e-mail invalide";
  if (v("website") && !WEBSITE.test(v("website")))
    errors.website = "Site web invalide";
  if (v("managerPhone") && !PHONE.test(v("managerPhone")))
    errors.managerPhone = "Numéro de téléphone invalide";
  if (v("managerEmail") && !EMAIL.test(v("managerEmail")))
    errors.managerEmail = "Adresse e-mail invalide";
  if (v("timbre")) {
    const timbre = Number(v("timbre").replace(",", "."));
    if (Number.isNaN(timbre) || timbre < 0 || timbre > 1000)
      errors.timbre = "Le timbre fiscal doit être compris entre 0 et 1000";
  }
  return errors;
};

const SERVER_ERRORS = {
  forbidden: "Vous n'avez pas le droit de modifier les informations de la société",
  no_company: "Aucune société n'est associée à votre compte",
  user_not_found: "Utilisateur introuvable",
  invalid_format: "Format du logo invalide (PNG ou JPEG uniquement)",
  file_too_large: "Le logo ne doit pas dépasser 2 Mo",
};

const STATUS_BADGE = { ACTIF: "badge-success", EXPIRE: "badge-danger", ANNULE: "badge-secondary" };
const STATUS_LABEL = { ACTIF: "Actif", EXPIRE: "Expiré", ANNULE: "Remplacé" };

const formatIsoDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = String(iso).split("-");
  return `${d}/${m}/${y}`;
};

// whole days from today to the given yyyy-MM-dd date (negative when past)
const daysUntil = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map(Number);
  const end = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end - today) / 86400000);
};

const PAYMENT_MODE = { VIREMENT: "virement", CHEQUE: "chèque", ESPECES: "espèces", MOBILE: "paiement mobile" };

const formatDay = (value) => (value ? new Date(value).toLocaleDateString("fr-FR") : "");

const GREEN = "#28a745";

// Bootstrap's focus rings are blue: the page is green (the colour of the brand)
const GreenStyle = () => (
  <style>{`
    .noryx-green .form-control:focus:not(.is-invalid) {
      border-color: ${GREEN};
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
  `}</style>
);

const parseIso = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// whole days from a yyyy-MM-dd date to another one (null when one of them is missing)
const daysBetween = (fromIso, toIso) => {
  const from = parseIso(fromIso);
  const to = parseIso(toIso);
  return from && to ? Math.round((to - from) / 86400000) : null;
};

const MaSociete = () => {
  // payments declared for the subscription (newest first) and the payment dialog
  const [payments, setPayments] = useState([]);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [cancelingPayment, setCancelingPayment] = useState(false);
  // subscription of the connected user's company (contracts managed by the partner)
  const [contracts, setContracts] = useState([]);
  const [contractsState, setContractsState] = useState("loading"); // loading | ready | error
  const [downloadingId, setDownloadingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // "no_company" | "network" | other
  const [canEdit, setCanEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(EMPTY);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBroken, setLogoBroken] = useState(false);
  const [alert, setAlert] = useState(null);
  const fileInput = useRef(null);

  const dirty =
    editing &&
    (logoFile !== null ||
      Object.keys(EMPTY).some((key) => form[key] !== saved[key]));

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    MyCompanyService.get()
      .then((response) => {
        const data = response.data;
        if (data && data.success) {
          const next = toForm(data.success);
          setSaved(next);
          setForm(next);
          setLogoUrl(data.success.fileUrl || null);
          setLogoBroken(false);
          setCanEdit(!!data.success.canEdit);
        } else {
          setLoadError((data && data.error) || "network");
        }
      })
      .catch(() => setLoadError("network"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    SocieteAndUserService.getSocieteByIdUser()
      .then((response) => {
        const societe = response.data && response.data.Societe;
        if (!societe || typeof societe !== "object" || societe.id === undefined) {
          throw new Error("no company");
        }
        return ClientContractService.getBySociete(societe.id);
      })
      .then((response) => {
        if (cancelled) return;
        if (response.data && Array.isArray(response.data.success)) {
          setContracts(response.data.success);
          setContractsState("ready");
        } else {
          setContractsState("error");
        }
      })
      .catch(() => {
        if (!cancelled) setContractsState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPayments = useCallback(() => {
    SubscriptionPaymentService.mine()
      .then((response) => {
        if (response.data && Array.isArray(response.data.success)) {
          setPayments(response.data.success);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const cancelPayment = (payment) => {
    if (cancelingPayment) return;
    setCancelingPayment(true);
    SubscriptionPaymentService.cancel(payment.id)
      .then((response) => {
        if (response.data && response.data.success) {
          setAlert({ severity: "success", text: "Demande de paiement annulée" });
        } else {
          setAlert({ severity: "error", text: "Cette demande ne peut plus être annulée" });
        }
        loadPayments();
      })
      .catch(() => setAlert({ severity: "error", text: "Erreur : problème de connexion" }))
      .finally(() => setCancelingPayment(false));
  };

  const downloadContract = async (contract) => {
    setDownloadingId(contract.id);
    try {
      const [{ pdf }, fileSaver, { default: ClientContractPdf }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("file-saver"),
        import("../components/invoices/ClientContractPdf"),
      ]);
      // file-saver is a CommonJS module: a dynamic import only exposes it as `default` (a named `saveAs` is undefined)
      const saveAs = fileSaver.saveAs || fileSaver.default;
      const client = {
        title: saved.title,
        matricule: saved.matricule,
        adresse: saved.adresse,
        email: saved.email,
        numPhone: saved.numPhone,
      };
      const blob = await pdf(
        <ClientContractPdf contract={contract} client={client} extras={{ clientLegalForm: saved.legalForm }} />
      ).toBlob();
      saveAs(blob, `Contrat_${contract.ref || "abonnement"}.pdf`);
    } catch (e) {
      console.error("downloadContract", e);
      setAlert({ severity: "error", text: "Erreur lors de la génération du contrat" });
    } finally {
      setDownloadingId(null);
    }
  };

  // warn when leaving with unsaved changes: closing the tab / reloading ...
  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    // ... and clicking an in-app link (menu, breadcrumbs ...)
    const onLinkClick = (event) => {
      const link = event.target.closest && event.target.closest("a[href]");
      if (!link || link.target === "_blank") return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      if (
        !window.confirm(
          "Des modifications ne sont pas enregistrées. Voulez-vous quitter cette page ?"
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", onLinkClick, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", onLinkClick, true);
    };
  }, [dirty]);

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  const setField = (key) => (event) => {
    const value = event.target.value;
    setForm((old) => ({ ...old, [key]: value }));
    if (errors[key]) setErrors((old) => ({ ...old, [key]: undefined }));
  };

  const onChooseLogo = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      setErrors((old) => ({ ...old, logo: "Format invalide : PNG ou JPEG uniquement" }));
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      setErrors((old) => ({ ...old, logo: "Le logo ne doit pas dépasser 2 Mo" }));
      return;
    }
    setErrors((old) => ({ ...old, logo: undefined }));
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoBroken(false);
  };

  const removeNewLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setErrors((old) => ({ ...old, logo: undefined }));
  };

  const onEdit = () => {
    setErrors({});
    setEditing(true);
  };

  const onCancel = () => {
    if (
      dirty &&
      !window.confirm("Annuler les modifications non enregistrées ?")
    ) {
      return;
    }
    setForm(saved);
    setErrors({});
    setLogoFile(null);
    setLogoPreview(null);
    setEditing(false);
  };

  const showError = (text) => setAlert({ severity: "error", text });

  const onSave = async () => {
    const clientErrors = validate(form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      showError("Veuillez corriger les erreurs du formulaire");
      return;
    }
    setSaving(true);
    try {
      // 1) logo (kept apart from the other fields: it has its own endpoint)
      let newLogoUrl = logoUrl;
      if (logoFile) {
        const logoResponse = await MyCompanyService.uploadLogo(logoFile);
        if (logoResponse.data && logoResponse.data.success) {
          newLogoUrl = logoResponse.data.success;
        } else {
          const code = logoResponse.data && logoResponse.data.error;
          showError(SERVER_ERRORS[code] || "Erreur lors de l'envoi du logo");
          setSaving(false);
          return;
        }
      }
      // 2) company fields
      const payload = { ...form };
      payload.timbre = form.timbre.trim()
        ? Number(form.timbre.trim().replace(",", "."))
        : null;
      const response = await MyCompanyService.update(payload);
      const data = response.data;
      if (data && data.success) {
        const next = toForm(data.success);
        setSaved(next);
        setForm(next);
        setLogoUrl(newLogoUrl);
        setLogoBroken(false);
        setLogoFile(null);
        setLogoPreview(null);
        setErrors({});
        setEditing(false);
        // documents (factures, devis ...) read the company logo from here
        if (newLogoUrl) localStorage.setItem("fileUrl", newLogoUrl);
        setAlert({ severity: "success", text: "Informations enregistrées avec succès" });
      } else if (data && data.error === "validation") {
        setErrors(data.fieldErrors || {});
        showError("Veuillez corriger les erreurs du formulaire");
      } else {
        showError(
          SERVER_ERRORS[data && data.error] || "Erreur : enregistrement impossible"
        );
      }
    } catch (e) {
      // the typed data stays in the form: nothing is lost
      showError("Erreur réseau : vos modifications n'ont pas été enregistrées");
    } finally {
      setSaving(false);
    }
  };

  // ---- presentation ------------------------------------------------------------------------------------

  // a field: an input in edition, a readable value (with a link for the mail, phone and web site) otherwise
  const field = (key, label, options = {}) => {
    const { required, type = "text", placeholder, col = "col-md-4", kind } = options;
    const error = errors[key];
    const value = form[key];
    if (!editing) {
      let content = <span className="text-muted">—</span>;
      if (value && value.trim()) {
        const text = value.trim();
        if (kind === "email") content = <a href={`mailto:${text}`}>{text}</a>;
        else if (kind === "tel") content = <a href={`tel:${text.replace(/\s/g, "")}`}>{text}</a>;
        else if (kind === "web")
          content = (
            <a href={/^https?:/i.test(text) ? text : `https://${text}`} target="_blank" rel="noopener noreferrer">
              {text}
            </a>
          );
        else content = text;
      }
      return (
        <div className={col}>
          <div className="mb-3">
            <div style={labelStyle}>{label}</div>
            <div style={{ minHeight: 24, wordBreak: "break-word" }}>{content}</div>
          </div>
        </div>
      );
    }
    return (
      <div className={col}>
        <div className="form-group">
          <label htmlFor={`company-${key}`} style={labelStyle}>
            {label}
            {required && <span style={{ color: "red" }}> *</span>}
          </label>
          <input
            id={`company-${key}`}
            type={type}
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={value}
            onChange={setField(key)}
            disabled={saving}
            placeholder={placeholder || ""}
          />
          {error && <div className="invalid-feedback d-block">{error}</div>}
        </div>
      </div>
    );
  };

  const section = (icon, title, children, subtitle) => (
    <div className="card card-outline card-success shadow-sm mb-3" style={{ borderRadius: 8 }}>
      <div className="card-header">
        <h3 className="card-title">
          <i className={`${icon} mr-2 text-success`}></i>
          {title}
        </h3>
        {subtitle && (
          <div className="text-muted small" style={{ clear: "both" }}>
            {subtitle}
          </div>
        )}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );

  const currentContract = contracts.find((c) => c.status === "ACTIF") || contracts[0] || null;
  const otherContracts = contracts.filter((c) => !currentContract || c.id !== currentContract.id);
  const remaining =
    currentContract && currentContract.status === "ACTIF" ? daysUntil(currentContract.endDate) : null;

  const downloadButton = (contract, label, className = "btn btn-outline-success btn-sm") => (
    <button
      className={className}
      disabled={downloadingId === contract.id}
      onClick={() => downloadContract(contract)}
      title="Télécharger le contrat (PDF)"
    >
      {downloadingId === contract.id ? (
        <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
      ) : (
        <i className="fas fa-file-pdf mr-1"></i>
      )}
      {label}
    </button>
  );

  // the badge of the subscription shown in the header card
  const subscriptionChip = () => {
    if (contractsState !== "ready" || !currentContract) return null;
    if (currentContract.status !== "ACTIF") {
      return (
        <span className={"badge p-2 " + (STATUS_BADGE[currentContract.status] || "badge-light")}>
          <i className="fas fa-file-contract mr-1"></i>Abonnement {(STATUS_LABEL[currentContract.status] || "").toLowerCase()}
        </span>
      );
    }
    const soon = remaining !== null && remaining <= 30;
    return (
      <span className={"badge p-2 " + (soon ? "badge-warning" : "badge-success")}>
        <i className="fas fa-file-contract mr-1"></i>
        Abonnement actif
        {soon && remaining >= 0 && ` · ${remaining === 0 ? "expire aujourd'hui" : `${remaining} j restant${remaining > 1 ? "s" : ""}`}`}
      </span>
    );
  };

  const subscriptionBody = () => {
    if (contractsState === "loading") return <span className="text-muted">Chargement…</span>;
    if (contractsState === "error")
      return <span className="text-muted">Les informations de l'abonnement sont indisponibles pour le moment.</span>;
    if (!currentContract)
      return <span className="text-muted">Aucun abonnement n'est enregistré pour votre société.</span>;
    const pendingPayment = payments.find((p) => p.status === "EN_ATTENTE");
    const lastPayment = payments[0];
    const total = daysBetween(currentContract.startDate, currentContract.endDate);
    const elapsed = daysBetween(currentContract.startDate, todayIso());
    const percent = total && total > 0 && elapsed !== null ? Math.max(0, Math.min(100, Math.round((elapsed / total) * 100))) : 0;
    const expired = remaining !== null && remaining < 0;
    const soon = remaining !== null && remaining >= 0 && remaining <= 30;
    const barClass = expired ? "bg-danger" : soon ? "bg-warning" : "bg-success";
    return (
      <>
        {pendingPayment && (
          <div className="alert alert-warning">
            <div>
              <b>Paiement envoyé</b> le {formatDay(pendingPayment.creationDate)} :{" "}
              {Number(pendingPayment.amount).toFixed(3)} TND ({PAYMENT_MODE[pendingPayment.mode] || pendingPayment.mode}).
              En attente de validation.
            </div>
            <button
              className="btn btn-sm btn-outline-dark mt-2"
              disabled={cancelingPayment}
              onClick={() => cancelPayment(pendingPayment)}
            >
              Annuler la demande
            </button>
          </div>
        )}
        {!pendingPayment && lastPayment && lastPayment.status === "REFUSE" && (
          <div className="alert alert-danger">
            <b>Paiement refusé</b> le {formatDay(lastPayment.decisionDate)}.
            {lastPayment.refusalReason && <> Motif : {lastPayment.refusalReason}</>} Vous pouvez envoyer un nouveau paiement.
          </div>
        )}
        {!pendingPayment && lastPayment && lastPayment.status === "VALIDE" && (
          <div className="alert alert-success">
            <b>Paiement validé</b> le {formatDay(lastPayment.decisionDate)} : votre abonnement a été prolongé.
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className={"badge " + (STATUS_BADGE[currentContract.status] || "badge-light")}>
            {STATUS_LABEL[currentContract.status] || currentContract.status}
          </span>
          <span className="text-muted small">Contrat {currentContract.ref}</span>
        </div>

        <div className="progress mb-1" style={{ height: 8 }} aria-hidden="true">
          <div className={"progress-bar " + barClass} style={{ width: `${percent}%` }}></div>
        </div>
        <div className="d-flex justify-content-between small text-muted mb-3">
          <span>{formatIsoDate(currentContract.startDate)}</span>
          <span className={expired ? "text-danger font-weight-bold" : soon ? "text-warning font-weight-bold" : ""}>
            {remaining === null
              ? ""
              : expired
              ? "Expiré"
              : remaining === 0
              ? "Expire aujourd'hui"
              : `${remaining} jour${remaining > 1 ? "s" : ""} restant${remaining > 1 ? "s" : ""}`}
          </span>
          <span>{formatIsoDate(currentContract.endDate)}</span>
        </div>

        <div className="row mb-2">
          <div className="col-6">
            <div style={labelStyle}>Durée</div>
            <div>{currentContract.periodicity === "ANNUEL" ? "Annuel" : "Mensuel"}</div>
          </div>
          <div className="col-6">
            <div style={labelStyle}>Montant HT</div>
            <div>{Number(currentContract.amount).toFixed(3)} TND</div>
          </div>
        </div>

        <div className="mt-3">
          {!pendingPayment && (
            <button className="btn btn-success btn-block mb-2" onClick={() => setPaymentDialog(true)}>
              <i className="fas fa-credit-card mr-2"></i>Payer mon abonnement
            </button>
          )}
          {downloadButton(currentContract, "Télécharger le contrat", "btn btn-outline-success btn-block")}
        </div>

        {otherContracts.length > 0 && (
          <div className="mt-4">
            <div style={labelStyle} className="mb-1">
              Contrats précédents
            </div>
            <ul className="list-group list-group-flush">
              {otherContracts.map((c) => (
                <li key={c.id} className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <div>
                    <div>{c.ref}</div>
                    <div className="small text-muted">
                      {formatIsoDate(c.startDate)} - {formatIsoDate(c.endDate)}{" "}
                      <span className={"badge " + (STATUS_BADGE[c.status] || "badge-light")}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                    </div>
                  </div>
                  {downloadButton(c, "Contrat")}
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  const shownLogo = logoPreview || (logoBroken ? null : logoSrc(logoUrl));

  const companyName = (saved.title || "").trim();
  const companyInitials = companyName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

  return (
    <div className="content-wrapper noryx-green" style={{ marginTop: "0" }}>
      <GreenStyle />
      <section className="content-header">
        <div className="container-fluid">
          <h4 className="mb-1">
            <i className="fas fa-building mr-2 text-success"></i>Ma société
          </h4>
          <small className="text-muted">Consultez et gérez les informations de votre société</small>
        </div>
      </section>

      <div className="content">
        <div className="container-fluid">
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status">
                <span className="sr-only">Chargement...</span>
              </div>
              <div className="text-muted mt-2">Chargement des informations...</div>
            </div>
          )}

          {!loading && loadError === "no_company" && (
            <div className="text-center text-muted py-5">
              <i className="fas fa-building fa-2x mb-2 d-block"></i>
              Aucune société n'est associée à votre compte.
              <div className="small">
                Contactez un administrateur pour rattacher votre compte à une société.
              </div>
            </div>
          )}

          {!loading && loadError && loadError !== "no_company" && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center">
              <span>
                {loadError === "network"
                  ? "Impossible de charger les informations (problème de connexion)."
                  : SERVER_ERRORS[loadError] || "Erreur lors du chargement des informations."}
              </span>
              <button className="btn btn-sm btn-outline-danger" onClick={load}>
                <i className="fas fa-redo mr-1"></i>Réessayer
              </button>
            </div>
          )}

          {!loading && !loadError && (
            <>
              <div className="card shadow-sm mb-3" style={{ borderRadius: 8, borderTop: `3px solid ${GREEN}` }}>
                <div className="card-body d-flex flex-wrap align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center bg-white border rounded mr-3 mb-2 mb-sm-0 flex-shrink-0"
                    style={{ width: 88, height: 88, overflow: "hidden" }}
                  >
                    {shownLogo ? (
                      <img
                        src={shownLogo}
                        alt="Logo de la société"
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        onError={() => setLogoBroken(true)}
                      />
                    ) : (
                      <span
                        className="d-flex align-items-center justify-content-center text-white font-weight-bold"
                        style={{ width: "100%", height: "100%", backgroundColor: GREEN, fontSize: 28 }}
                        aria-hidden="true"
                      >
                        {companyInitials || <i className="fas fa-building"></i>}
                      </span>
                    )}
                  </div>
                  <div className="flex-grow-1 mr-3">
                    <h4 className="mb-1">{companyName || "Ma société"}</h4>
                    {saved.tradeName && <div className="text-muted">{saved.tradeName}</div>}
                    <div className="mt-1">
                      {saved.matricule && (
                        <span className="badge badge-light border mr-1">Matricule {saved.matricule}</span>
                      )}
                      {saved.legalForm && <span className="badge badge-light border mr-1">{saved.legalForm}</span>}
                      {saved.city && (
                        <span className="badge badge-light border mr-1">
                          <i className="fas fa-map-marker-alt mr-1"></i>
                          {saved.city}
                        </span>
                      )}
                      {subscriptionChip()}
                    </div>
                  </div>
                  <div className="mt-2 mt-sm-0">
                    {!editing && canEdit && (
                      <button className="btn btn-success" onClick={onEdit}>
                        <i className="fas fa-pencil-alt mr-1"></i>
                        Modifier
                      </button>
                    )}
                    {!editing && !canEdit && (
                      <span className="badge badge-secondary p-2">
                        <i className="fas fa-lock mr-1"></i>Lecture seule
                      </span>
                    )}
                    {editing && (
                      <span className="badge badge-warning p-2">
                        <i className="fas fa-pencil-alt mr-1"></i>Modification en cours
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {editing && (
                <div className="alert alert-light border small">
                  <i className="fas fa-info-circle mr-1"></i>
                  Les champs marqués d'une étoile sont obligatoires.
                </div>
              )}

              <div className="row">
                <div className="col-lg-8">
                  {section(
                    "fas fa-id-card",
                    "Informations générales",
                    <div className="row">
                      {field("title", "Raison sociale", { required: true, col: "col-md-6" })}
                      {field("tradeName", "Nom commercial", { col: "col-md-6" })}
                      {field("matricule", "Matricule fiscal")}
                      {field("commerceRegister", "Registre de commerce")}
                      {field("legalForm", "Forme juridique", { placeholder: "SARL, SA, ..." })}
                      {field("activitySector", "Secteur d'activité", {
                        placeholder: "Commerce, Industrie, Services, ...",
                        col: "col-md-12",
                      })}
                      {field("adresse", "Adresse", { col: "col-md-12" })}
                      {field("city", "Ville")}
                      {field("postalCode", "Code postal")}
                      {field("country", "Pays")}
                    </div>
                  )}

                  {section(
                    "fas fa-address-book",
                    "Coordonnées",
                    <>
                      <div className="row">
                        {field("numPhone", "Téléphone professionnel", { type: "tel", kind: "tel" })}
                        {field("email", "E-mail professionnel", { type: "email", kind: "email" })}
                        {field("website", "Site web", { placeholder: "www.exemple.com", kind: "web" })}
                      </div>
                      <hr className="mt-0" />
                      <div className="row">
                        {field("managerName", "Responsable")}
                        {field("managerPhone", "Téléphone du responsable", { type: "tel", kind: "tel" })}
                        {field("managerEmail", "E-mail du responsable", { type: "email", kind: "email" })}
                      </div>
                    </>
                  )}

                  {section(
                    "fas fa-file-invoice-dollar",
                    "Facturation",
                    <div className="row">{field("timbre", "Timbre fiscal", { placeholder: "0.000" })}</div>
                  )}
                </div>

                <div className="col-lg-4">
                  {section(
                    "fas fa-image",
                    "Logo",
                    <div className="text-center">
                      <div
                        className="d-flex align-items-center justify-content-center bg-light border rounded mb-3 mx-auto"
                        style={{ width: "100%", height: 150 }}
                      >
                        {shownLogo ? (
                          <img
                            src={shownLogo}
                            alt="Logo de la société"
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                            onError={() => setLogoBroken(true)}
                          />
                        ) : (
                          <span className="text-muted small text-center">
                            <i className="fas fa-image fa-2x d-block mb-1"></i>
                            {logoBroken ? "Logo introuvable" : "Aucun logo"}
                          </span>
                        )}
                      </div>
                      {editing ? (
                        <>
                          <input
                            ref={fileInput}
                            type="file"
                            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                            style={{ display: "none" }}
                            onChange={onChooseLogo}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-success btn-block"
                            onClick={() => fileInput.current && fileInput.current.click()}
                            disabled={saving}
                          >
                            <i className="fas fa-upload mr-1"></i>
                            {logoUrl ? "Remplacer le logo" : "Ajouter un logo"}
                          </button>
                          {logoFile && (
                            <button
                              type="button"
                              className="btn btn-link text-danger btn-block"
                              onClick={removeNewLogo}
                              disabled={saving}
                            >
                              Annuler le nouveau logo
                            </button>
                          )}
                          <div className="small text-muted mt-2">
                            PNG ou JPEG, 2 Mo maximum.
                            {logoFile && " Le logo sera enregistré avec les autres informations."}
                          </div>
                          {errors.logo && <div className="text-danger small">{errors.logo}</div>}
                        </>
                      ) : (
                        <span className="text-muted small">
                          Le logo apparaît sur vos factures, devis et autres documents.
                        </span>
                      )}
                    </div>
                  )}

                  {section("fas fa-file-contract", "Abonnement", subscriptionBody())}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {editing && (
        <div
          className="bg-white border-top shadow d-flex flex-wrap justify-content-between align-items-center py-2 px-3"
          style={{ position: "sticky", bottom: 0, zIndex: 10 }}
        >
          <span className={dirty ? "text-warning font-weight-bold" : "text-muted"}>
            <i className={`fas ${dirty ? "fa-exclamation-circle" : "fa-info-circle"} mr-1`}></i>
            {dirty ? "Modifications non enregistrées" : "Aucune modification"}
          </span>
          <div>
            <button className="btn btn-outline-secondary mr-2" onClick={onCancel} disabled={saving}>
              Annuler
            </button>
            <button className="btn btn-success" onClick={onSave} disabled={saving}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Enregistrement...
                </>
              ) : (
                <>
                  <i className="fas fa-check mr-1"></i>
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {paymentDialog && currentContract && (
        <SubscriptionPaymentDialog
          contract={currentContract}
          onClose={() => setPaymentDialog(false)}
          onSent={() => {
            setAlert({ severity: "success", text: "Paiement envoyé, en attente de validation" });
            loadPayments();
          }}
        />
      )}

      {alert && (
        <Snackbar
          open
          autoHideDuration={3500}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert variant="filled" onClose={() => setAlert(null)} severity={alert.severity}>
            {alert.text}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default MaSociete;
